// Frontend mirror of the backend MetadataInheritanceResolver (05 §D-010).
// Used by the editor for optimistic UI so inherited/overridden fields render correctly
// before the server round-trip completes. Unit-tested to parity.

export type MetadataOrigin = 'book' | 'chapter' | 'paragraph';

export interface BookMetadata {
  pov?: string | null;
  narrativeStyle?: string | null;
  tense?: string | null;
  genre?: string | null;
  mood?: string | null;
  tone?: string | null;
}

export interface ChapterMetadata {
  povOverride?: string | null;
  narrativeStyleOverride?: string | null;
  tenseOverride?: string | null;
}

export interface ParagraphMetadata {
  mood?: string | null;
  tone?: string | null;
  sceneDescription?: string | null;
}

export interface FieldValue {
  value: string | null | undefined;
  origin: MetadataOrigin;
}

export interface EffectiveMetadata {
  pov: FieldValue;
  narrativeStyle: FieldValue;
  tense: FieldValue;
  mood: FieldValue;
  tone: FieldValue;
  genre: FieldValue;
  sceneDescription: FieldValue;
}

function resolve(
  paragraph: string | null | undefined,
  chapter: string | null | undefined,
  book: string | null | undefined,
): FieldValue {
  if (typeof paragraph === 'string' && paragraph.trim().length > 0) {
    return { value: paragraph, origin: 'paragraph' };
  }
  if (typeof chapter === 'string' && chapter.trim().length > 0) {
    return { value: chapter, origin: 'chapter' };
  }
  return { value: book ?? null, origin: 'book' };
}

export function resolveEffectiveMetadata(
  book: BookMetadata,
  chapter: ChapterMetadata | null | undefined,
  paragraph: ParagraphMetadata | null | undefined,
): EffectiveMetadata {
  return {
    pov: resolve(undefined, chapter?.povOverride, book.pov),
    narrativeStyle: resolve(
      undefined,
      chapter?.narrativeStyleOverride,
      book.narrativeStyle,
    ),
    tense: resolve(undefined, chapter?.tenseOverride, book.tense),
    mood: resolve(paragraph?.mood, undefined, book.mood),
    tone: resolve(paragraph?.tone, undefined, book.tone),
    genre: resolve(undefined, undefined, book.genre),
    sceneDescription: resolve(
      paragraph?.sceneDescription,
      undefined,
      undefined,
    ),
  };
}
